'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import type { Post } from '@/lib/api'
import { getCategoryColor } from '@/lib/constants'
import { useVideoModal } from './VideoModalProvider'

interface PostCardProps {
  post: Post
  variant?: 'default' | 'horizontal' | 'minimal'
  showExcerpt?: boolean
  showCategory?: boolean
}

export default function PostCard({ post, variant = 'default', showExcerpt = true, showCategory = true }: PostCardProps) {
  const locale = useLocale()
  const t = useTranslations('categories')
  const ta = useTranslations('article')
  const colors = getCategoryColor(post.category)
  const href = `/${locale}/${post.category}/${post.slug}`
  const { openModal } = useVideoModal()
  
  // Utiliser le nom dynamique de la catégorie si disponible, sinon fallback sur les traductions statiques
  const getCategoryName = () => {
    if (post.category_name) {
      return post.category_name
    }
    // Fallback sur les traductions statiques pour les catégories existantes
    try {
      return t(post.category)
    } catch {
      // Si la traduction n'existe pas, retourner le slug en format lisible
      return post.category.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
  }
  
  const formatDate = (date: string) => {
    const d = new Date(date)
    return d.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
  }

  // Variant horizontal - Pour sidebar et listes (style Noovo)
  if (variant === 'horizontal') {
    return (
      <>
        <article className="group overflow-hidden rounded-xl bg-white shadow-sm transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
          <div className="flex gap-4">
            <div className="relative h-32 w-48 flex-shrink-0 overflow-hidden rounded-lg">
              <Image
                src={post.coverImage}
                alt={post.title}
                fill
                className="object-cover transition-all duration-500 group-hover:scale-110"
                sizes="192px"
              />
              {/* Overlay gradient au hover */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              
              {post.type === 'video' && (
                <>
                  {post.duration && (
                    <div className="absolute bottom-2 right-2 rounded bg-black/75 px-2 py-1 text-xs font-medium text-white">
                      {post.duration}
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center z-20">
                    <button
                      onClick={(e) => {
                        console.log('Video button clicked (horizontal)!', post.title)
                        e.preventDefault()
                        e.stopPropagation()
                        if (post.youtubeId) {
                          openModal(post.youtubeId, post.title)
                        }
                      }}
                      onTouchStart={(e) => {
                        console.log('Video button touched (horizontal)!', post.title)
                        // Empêche le double-tap sur mobile
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      className="flex h-12 w-12 items-center justify-center rounded-full bg-red-600 text-white shadow-lg transition-all hover:bg-red-700 hover:scale-110 focus:outline-none focus:ring-4 focus:ring-red-500/50 active:scale-95 relative z-10"
                      aria-label="Regarder la vidéo"
                    >
                      <svg className="h-6 w-6 ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </button>
                  </div>
                  <div className="absolute left-2 top-2 rounded bg-red-600 px-2 py-1 text-xs font-bold text-white">
                    VIDÉO
                  </div>
                </>
              )}
            </div>
            
            <div className="flex-1 p-4">
              {showCategory && (
                <div className={`mb-2 inline-flex items-center gap-2 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${colors.text} ${colors.border}`}>
                  <span className={`inline-block h-1.5 w-1.5 rounded-full ${colors.bg}`}></span>
                  {getCategoryName()}
                </div>
              )}
              
              <Link href={href}>
                <h3 className="mb-2 line-clamp-2 text-lg font-bold leading-tight text-gray-900 transition-colors hover:text-blue-600 cursor-pointer">
                  {post.title}
                </h3>
              </Link>
              
              <p className="text-sm text-gray-500">{formatDate(post.publishedAt)}</p>
            </div>
          </div>
        </article>
        

      </>
    )
  }

  // Variant minimal - Pour listes compactes
  if (variant === 'minimal') {
    return (
      <>
        <article className="group">
          <div className="flex gap-3">
            <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg">
              <Image
                src={post.coverImage}
                alt={post.title}
                fill
                className="object-cover transition-transform group-hover:scale-105"
                sizes="80px"
              />
              
              {post.type === 'video' && (
                <>
                  <div className="absolute inset-0 flex items-center justify-center z-20">
                    <button
                      onClick={(e) => {
                        console.log('Video button clicked (minimal)!', post.title)
                        e.preventDefault()
                        e.stopPropagation()
                        if (post.youtubeId) {
                          openModal(post.youtubeId, post.title)
                        }
                      }}
                      onTouchStart={(e) => {
                        console.log('Video button touched (minimal)!', post.title)
                        // Empêche le double-tap sur mobile
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white shadow-lg transition-all hover:bg-red-700 hover:scale-110 focus:outline-none focus:ring-4 focus:ring-red-500/50 active:scale-95 relative z-10"
                      aria-label="Regarder la vidéo"
                    >
                      <svg className="h-4 w-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </button>
                  </div>
                  <div className="absolute left-1 top-1 rounded bg-red-600 px-1 py-0.5 text-xs font-bold text-white">
                    VID
                  </div>
                </>
              )}
            </div>
            
            <div className="flex-1">
              <div className={`mb-1 inline-block rounded px-2 py-0.5 text-xs font-semibold ${colors.bg} text-white`}>
                {getCategoryName()}
              </div>
              
              <Link href={href}>
                <h3 className="mb-1 line-clamp-2 text-sm font-bold leading-tight text-gray-900 transition-colors hover:text-blue-600 cursor-pointer">
                  {post.title}
                </h3>
              </Link>
              
              <p className="text-xs text-gray-500">{formatDate(post.publishedAt)}</p>
            </div>
          </div>
        </article>
        

      </>
    )
  }

  // Variant par défaut - Style magazine inspiré Noovo
  return (
    <>
      <article className="group overflow-hidden rounded-xl bg-white shadow-sm transition-all duration-300 hover:shadow-2xl hover:-translate-y-2">
        <div className="relative aspect-[16/9] w-full overflow-hidden bg-gray-100">
          <Image
            src={post.coverImage}
            alt={post.title}
            fill
            className="object-cover transition-all duration-500 group-hover:scale-110"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
          {/* Overlay gradient au hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
          
          {post.type === 'video' && (
            <>
              {post.duration && (
                <div className="absolute bottom-3 right-3 rounded bg-black/75 px-2 py-1 text-sm font-medium text-white">
                  {post.duration}
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center z-20">
                <button
                  onClick={(e) => {
                    console.log('Video button clicked!', post.title)
                    e.preventDefault()
                    e.stopPropagation()
                    if (post.youtubeId) {
                      openModal(post.youtubeId, post.title)
                    }
                  }}
                  onTouchStart={(e) => {
                    console.log('Video button touched!', post.title)
                    // Empêche le double-tap sur mobile
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  className="flex h-16 w-16 items-center justify-center rounded-full bg-red-600 text-white shadow-lg transition-all hover:bg-red-700 hover:scale-110 focus:outline-none focus:ring-4 focus:ring-red-500/50 active:scale-95 relative z-10"
                  aria-label="Regarder la vidéo"
                >
                  <svg className="h-8 w-8 ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </button>
              </div>
              <div className="absolute left-3 top-3 rounded bg-red-600 px-3 py-1 text-sm font-bold text-white">
                VIDÉO
              </div>
              <div className="absolute right-3 top-3 flex items-center gap-1 rounded bg-red-600 px-2 py-1 text-xs font-bold text-white">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                </svg>
                YouTube
              </div>
            </>
          )}
        </div>
        
        <div className="p-5">
          {showCategory && (
            <div className={`mb-3 inline-flex items-center gap-2 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${colors.text} ${colors.border}`}>
              <span className={`inline-block h-1.5 w-1.5 rounded-full ${colors.bg}`}></span>
              {getCategoryName()}
            </div>
          )}
          
          <Link href={href}>
            <h3 className="mb-2 line-clamp-2 text-lg font-bold leading-tight text-gray-900 transition-colors hover:text-blue-600 cursor-pointer">
              {post.title}
            </h3>
          </Link>
          
          {showExcerpt && (
            <p className="mb-3 line-clamp-2 text-sm text-gray-600 leading-snug">
              {post.excerpt}
            </p>
          )}
          
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>{formatDate(post.publishedAt)}</span>
            {post.readingTime && <span>{post.readingTime} {ta('readingTime')}</span>}
          </div>
        </div>
      </article>
      

    </>
  )
}